import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const AccessibilityControls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [textSize, setTextSize] = useState(1); // 1 = normal, 1.1 = large, 1.2 = xl
  const { resolvedTheme } = useTheme();

  const textSizeButtonStyle = (size, fontSize) => {
    const isActive = textSize === size;
    const isInactiveInLightMode = resolvedTheme === 'light' && !isActive;

    return {
      flex: 1,
      padding: '6px',
      background: isActive ? 'var(--color-primary)' : 'var(--color-surface-raised)',
      border: 'none',
      borderRadius: '4px',
      color: isInactiveInLightMode ? 'var(--color-text-main)' : '#fff',
      boxShadow: isInactiveInLightMode
        ? 'inset 0 0 0 1px var(--color-border-hover)'
        : 'none',
      cursor: 'pointer',
      fontSize,
    };
  };

  useEffect(() => {
    // Apply High Contrast
    if (highContrast) {
      document.body.classList.add('high-contrast-mode');
    } else {
      document.body.classList.remove('high-contrast-mode');
    }
  }, [highContrast]);

  useEffect(() => {
    // Apply Text Size by changing the root font size
    document.documentElement.style.fontSize = `${textSize * 16}px`;
  }, [textSize]);

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '24px',
        zIndex: 9999,
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            style={{
              position: 'absolute',
              bottom: '60px',
              left: '0',
              color: 'var(--color-text-main)',
              background: 'var(--color-surface-solid)',
              border: '2px solid var(--color-primary)',
              padding: '16px',
              borderRadius: '12px',
              width: '240px',
              boxShadow: 'var(--shadow-card-hover)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <h3 style={{ margin: 0, color: 'var(--color-text-main)', fontSize: '1rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '8px' }}>
              Accessibility
            </h3>

            {/* High Contrast Toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>High Contrast</span>
              <button
                onClick={() => setHighContrast(!highContrast)}
                style={{
                  background: highContrast ? 'var(--color-primary)' : 'var(--color-surface-raised)',
                  border: 'none',
                  borderRadius: '20px',
                  width: '40px',
                  height: '24px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
                aria-pressed={highContrast}
                aria-label="Toggle High Contrast"
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: highContrast ? '18px' : '2px',
                    width: '20px',
                    height: '20px',
                    background: '#fff',
                    borderRadius: '50%',
                    transition: 'left 0.3s'
                  }}
                />
              </button>
            </div>

            {/* Text Size Controls */}
            <div>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>Text Size</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setTextSize(1)}
                  style={textSizeButtonStyle(1, '0.9rem')}
                  aria-label="Normal Text Size"
                  aria-pressed={textSize === 1}
                >
                  A
                </button>
                <button
                  onClick={() => setTextSize(1.1)}
                  style={textSizeButtonStyle(1.1, '1rem')}
                  aria-label="Large Text Size"
                  aria-pressed={textSize === 1.1}
                >
                  A
                </button>
                <button
                  onClick={() => setTextSize(1.2)}
                  style={textSizeButtonStyle(1.2, '1.1rem')}
                  aria-label="Extra Large Text Size"
                  aria-pressed={textSize === 1.2}
                >
                  A
                </button>
              </div>
            </div>

            
            <button
               onClick={() => {
                 setHighContrast(false);
                 setTextSize(1);
               }}
               style={{
                 marginTop: '8px',
                 background: 'transparent',
                 border: '1px solid var(--color-border-hover)',
                 color: 'var(--color-text-muted)',
                 padding: '6px',
                 borderRadius: '4px',
                 cursor: 'pointer',
                 fontSize: '0.8rem'
               }}
            >
              Reset Settings
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Toggle Button */}
      <motion.button
        onClick={toggleOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'var(--color-primary)',
          border: 'none',
          boxShadow: '0 4px 12px color-mix(in srgb, var(--color-primary) 40%, transparent)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        aria-label="Accessibility Options"
        aria-expanded={isOpen}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 16v-4"></path>
          <path d="M12 8h.01"></path>
          <path d="M9 12h6"></path>
        </svg>
      </motion.button>
    </div>
  );
};

export default AccessibilityControls;
